import { FileUploadWorkerInfo } from "./file-upload-worker-info-model";
import { FileMultipartUploadWorkerInfo } from "./file-multipart-upload-worker-info-model";
import { apolloClient } from "../services/vue-apollo";
import {
    GET_PROJECT_ARTIFACT_UPLOAD_URL,
    GET_PROJECT_ARTIFACT_MULTIPART_INFO,
    COMPLETE_PROJECT_ARTIFACT_MULTIPART,
    REGISTER_PROJECT_ARTIFACT,
} from "../graphql/project-artifact-queries";

export class ProjectArtifactUploadWorkerInfo extends FileUploadWorkerInfo {
    projectId: number;
    artifactName: string;
    artifactType: string;

    constructor(file: File, projectId: number, artifactName: string, artifactType: string) {
        super(file);
        this.projectId = projectId;
        this.artifactName = artifactName;
        this.artifactType = artifactType;
    }

    startUpload() {
        apolloClient
            .query({
                query: GET_PROJECT_ARTIFACT_UPLOAD_URL,
                variables: {
                    projectId: this.projectId,
                    artifactType: this.artifactType,
                    filename: this.file.name
                },
                fetchPolicy: "no-cache",
            })
            .then((res) => {
                this.startSinglePartUpload(res.data.getProjectArtifactUploadUrl);
            })
            .catch((ex) => {
                console.error(ex);
                if (this.onError) {
                    this.onError(ex);
                }
            });
    }

    completeUpload() {
        this.onStarted?.(`Registering ${this.artifactType.toUpperCase()} artifact...`);
        apolloClient
            .mutate({
                mutation: REGISTER_PROJECT_ARTIFACT,
                variables: {
                    projectId: this.projectId,
                    name: this.artifactName || this.file.name,
                    artifactType: this.artifactType,
                    filename: this.file.name,
                    fileSize: this.file.size,
                    mimeType: this.file.type || undefined,
                },
            })
            .then(() => {
                super.completeUpload();
            })
            .catch((ex) => {
                this.progressMonitor.stopProgressMonitoring();
                this.state.setError(ex);
                if (this.onError) {
                    this.onError(ex);
                }
            });
    }
}

export class ProjectArtifactMultipartUploadWorkerInfo extends FileMultipartUploadWorkerInfo {
    projectId: number;
    artifactName: string;
    artifactType: string;

    constructor(file: File, projectId: number, artifactName: string, artifactType: string) {
        super(file);
        this.projectId = projectId;
        this.artifactName = artifactName;
        this.artifactType = artifactType;
    }

    startUpload() {
        apolloClient
            .query({
                query: GET_PROJECT_ARTIFACT_MULTIPART_INFO,
                variables: {
                    projectId: this.projectId,
                    artifactType: this.artifactType,
                    filename: this.file.name,
                    numberOfParts: this.numberOfParts,
                },
                fetchPolicy: "no-cache",
            })
            .then((res) => {
                const info = res.data.getProjectArtifactMultipartInfo;
                this.startMultipartUpload({ ...info, filename: this.file.name });
            })
            .catch((ex) => this.handleError(ex, () => this.startUpload()));
    }

    completeMultipartUpload(eTags: string[]) {
        this.progressMonitor.setComplete();
        this.state.setComplete();
        this.onStarted?.(`Registering ${this.artifactType.toUpperCase()} artifact...`);

        apolloClient
            .mutate({
                mutation: COMPLETE_PROJECT_ARTIFACT_MULTIPART,
                variables: {
                    projectId: this.projectId,
                    artifactType: this.artifactType,
                    filename: this.file.name,
                    uploadId: this.uploadId,
                    eTags: eTags.join(","),
                },
            })
            .then(() => {
                return apolloClient.mutate({
                    mutation: REGISTER_PROJECT_ARTIFACT,
                    variables: {
                        projectId: this.projectId,
                        name: this.artifactName || this.file.name,
                        artifactType: this.artifactType,
                        filename: this.file.name,
                        fileSize: this.file.size,
                        mimeType: this.file.type || undefined,
                    },
                });
            })
            .then(() => {
                if (this.onComplete) this.onComplete();
            })
            .catch((ex) => {
                if (this.onError) this.onError(ex);
            });
    }
}
